/**
 * Centralized Error Tracking Service
 * Tracks all errors from frontend and backend for debugging
 */

export interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  source: 'frontend' | 'backend' | 'api';
  component: string;
  action: string;
  error: {
    message: string;
    stack?: string;
    code?: string;
    status?: number;
  };
  context: {
    url?: string;
    endpoint?: string;
    userId?: string;
    userAgent?: string;
    [key: string]: any;
  };
  userInfo?: {
    userId?: string;
    email?: string;
    plan?: string;
  };
}

class ErrorTracker {
  private logs: ErrorLog[] = [];
  private maxLogs = 1000; // Keep last 1000 logs
  private isInitialized = false;

  /**
   * Initialize error tracker
   */
  init() {
    if (this.isInitialized) return;
    
    // Load previous logs from localStorage
    try {
      const saved = localStorage.getItem('orca_error_logs');
      if (saved) {
        this.logs = JSON.parse(saved);
      }
    } catch (error) {
      console.error('[ErrorTracker] Failed to load saved logs:', error);
    }

    // Listen for unhandled errors
    window.addEventListener('error', (event) => {
      this.trackError({
        message: event.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }, {
        source: 'frontend',
        component: 'global',
        action: 'unhandled-error'
      });
    });

    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        message: event.reason?.message || 'Unhandled promise rejection',
        stack: event.reason?.stack
      }, {
        source: 'frontend',
        component: 'global',
        action: 'unhandled-promise-rejection'
      });
    });

    this.isInitialized = true;
    console.log('[ErrorTracker] Initialized');
  }

  /**
   * Track an error
   */
  trackError(
    error: Error | { message: string; stack?: string; code?: string; status?: number; [key: string]: any },
    metadata: {
      source: 'frontend' | 'backend' | 'api';
      component: string;
      action: string;
      context?: Record<string, any>;
      userInfo?: { userId?: string; email?: string; plan?: string };
      skipLogging?: boolean; // Skip logging to console/file if true
    }
  ) {
    const errorLog: ErrorLog = {
      id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      level: 'error',
      source: metadata.source,
      component: metadata.component,
      action: metadata.action,
      error: {
        message: error.message || 'Unknown error',
        stack: error.stack,
        code: (error as any).code,
        status: (error as any).status
      },
      context: {
        ...metadata.context,
        userAgent: navigator.userAgent,
        url: window.location.href
      },
      userInfo: metadata.userInfo
    };

    // Skip logging if explicitly requested (for expected errors)
    if (metadata.skipLogging) {
      return errorLog;
    }

    // Add to logs
    this.logs.unshift(errorLog);
    
    // Keep only last maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Save to localStorage
    try {
      localStorage.setItem('orca_error_logs', JSON.stringify(this.logs));
    } catch (e) {
      console.error('[ErrorTracker] Failed to save logs:', e);
    }

    // Log to console with details
    console.error(`[ErrorTracker] ${metadata.source}/${metadata.component}/${metadata.action}:`, {
      message: error.message,
      context: metadata.context,
      timestamp: errorLog.timestamp
    });

    // Send to backend if available (non-blocking)
    this.sendToBackend(errorLog).catch(() => {
      // Silently fail - don't break the app
    });

    return errorLog;
  }

  /**
   * Track a warning
   */
  trackWarning(
    message: string,
    metadata: {
      source: 'frontend' | 'backend' | 'api';
      component: string;
      action: string;
      context?: Record<string, any>;
    }
  ) {
    const log: ErrorLog = {
      id: `warning-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      level: 'warning',
      source: metadata.source,
      component: metadata.component,
      action: metadata.action,
      error: { message },
      context: {
        ...metadata.context,
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    };

    this.logs.unshift(log);
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    try {
      localStorage.setItem('orca_error_logs', JSON.stringify(this.logs));
    } catch (e) {
      // Ignore
    }

    console.warn(`[ErrorTracker] ${metadata.source}/${metadata.component}/${metadata.action}:`, message);
  }

  /**
   * Track info (for debugging)
   */
  trackInfo(
    message: string,
    metadata: {
      source: 'frontend' | 'backend' | 'api';
      component: string;
      action: string;
      context?: Record<string, any>;
    }
  ) {
    const log: ErrorLog = {
      id: `info-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      level: 'info',
      source: metadata.source,
      component: metadata.component,
      action: metadata.action,
      error: { message },
      context: {
        ...metadata.context,
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    };

    this.logs.unshift(log);
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    try {
      localStorage.setItem('orca_error_logs', JSON.stringify(this.logs));
    } catch (e) {
      // Ignore
    }

    console.log(`[ErrorTracker] ${metadata.source}/${metadata.component}/${metadata.action}:`, message);
  }

  /**
   * Send error to backend (non-blocking)
   */
  private async sendToBackend(log: ErrorLog) {
    try {
      // Use apiRequest instead of fetch for consistency and error handling
      const { default: apiRequest } = await import('./api.js');
      await apiRequest<{ success: boolean }>('/logs/error', {
        method: 'POST',
        body: JSON.stringify(log)
      });
    } catch (error) {
      // Silently fail - don't break the app if error logging fails
      console.warn('[ErrorTracker] Failed to send error to backend:', error);
    }
  }

  /**
   * Get all logs
   */
  getLogs(limit?: number): ErrorLog[] {
    return limit ? this.logs.slice(0, limit) : this.logs;
  }

  /**
   * Get logs by filter
   */
  getLogsByFilter(filter: {
    source?: string;
    component?: string;
    level?: string;
    since?: Date;
  }): ErrorLog[] {
    return this.logs.filter(log => {
      if (filter.source && log.source !== filter.source) return false;
      if (filter.component && log.component !== filter.component) return false;
      if (filter.level && log.level !== filter.level) return false;
      if (filter.since && new Date(log.timestamp) < filter.since) return false;
      return true;
    });
  }

  /**
   * Clear logs
   */
  clearLogs() {
    this.logs = [];
    localStorage.removeItem('orca_error_logs');
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Download logs as file
   */
  downloadLogs() {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orca-error-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Singleton instance
export const errorTracker = new ErrorTracker();

// Auto-initialize
if (typeof window !== 'undefined') {
  errorTracker.init();
}

